/**
 * The guest's side of a lobby: say four things, believe everything else.
 *
 * A guest asks to be ready, says what it is called and who it is playing as, presents its token
 * when it comes back, and says goodbye when it goes.
 * Everything else it is told, because the host owns the session and a guest that decided anything
 * about the membership would be a second opinion on who is in the game.
 *
 * The token is the only thing kept across a dropped connection, and the only thing this side hands
 * back out: `onToken` fires whenever the host seats this machine, so whoever owns the session can
 * write it down and a reloaded page can present it. Where it is written down, and why that place
 * cannot let a stale tab walk into somebody else's chair, is
 * [carriedToken](../session/carriedToken.ts).
 *
 * Frames from anyone but the host are ignored. A guest holds exactly one connection so this cannot
 * normally happen, and reading a roster from whoever sent one is how a session gets a second
 * authority.
 */

import { decodeLobby, encodeLobby } from '@/net/wire/lobbyFrame';
import { leftNotice } from '@/net/lobby/lobbyNotices';
import { MESSAGE_KINDS } from '@/net/wire/messageKinds';
import { silentListeners } from '@/net/transportContract';
import type { CharacterSheet } from '@/sim/progression/characterSheet';
import type { LobbyEvent } from '@/net/lobby/lobbyState';
import type { LobbyMessage } from '@/net/wire/lobbyFrame';
import type { PeerId, Transport, TransportListeners } from '@/net/transportContract';
import type { SessionNotice } from '@/net/lobby/lobbyNotices';

export interface LobbyGuestOptions {
  readonly transport: Transport;
  /** What this machine calls itself. Absent leaves the host's placeholder standing. */
  readonly playerName?: string | undefined;
  /**
   * Who this machine is playing as. Absent leaves the seat with no character in it, and the host
   * gives a seat with no character no body, so a mode that wants one has to say.
   */
  readonly playerSheet?: CharacterSheet | undefined;
  readonly onEvent: (event: LobbyEvent) => void;
  readonly onNotice: (notice: SessionNotice) => void;
  /**
   * The token the host just seated this machine with, every time it says one.
   *
   * Said on the way out rather than read back later, because the seat message is the one moment
   * this side knows the token is current, and a page can be gone by the next one.
   */
  readonly onToken?: ((token: string) => void) | undefined;
}

export interface LobbyGuest {
  /** Handed to `relayTo`, so this sees the same events as everything else on the transport. */
  readonly listeners: TransportListeners;
  setReady(ready: boolean): void;
  /**
   * Says what this machine would like to be called, now and after every re-roll.
   *
   * The host is still the one that publishes it. This is a request in the same sense `ready` is:
   * the name reaches the room in the next roster or it does not reach it at all.
   */
  setName(name: string): void;
  /**
   * Says who this machine is playing as, now and after every change to the character.
   *
   * Unlike a name this is not cosmetic: the host builds this player's body from what it is told
   * here, and until it is told there is no body. Announced on every fresh connection for the same
   * reason the name is.
   */
  setSheet(sheet: CharacterSheet): void;
  /**
   * Says this side is listening now, and asks for what it may already have missed.
   *
   * The host seats a guest the moment the peer is announced, and this half is built a moment after
   * the connection resolves, so on an unlucky ordering the seat and the roster have been and gone
   * before anything was listening for them. Asking makes that a request the host answers rather
   * than a broadcast nobody caught.
   */
  announceArrival(): void;
  /** Says goodbye, so the host frees the seat instead of holding it for a quarter of a minute. */
  announceLeaving(): void;
  /** Presents the token this session was seated with. Does nothing before there is one. */
  presentToken(): void;
  /** What a reconnect has to carry to come back as the same player. Null before being seated. */
  token(): string | null;
  /** The token from an earlier connection, so a fresh transport can pick up the same seat. */
  adoptToken(token: string): void;
}

export function createLobbyGuest(options: LobbyGuestOptions): LobbyGuest {
  const { transport } = options;
  let token: string | null = null;
  /** What this machine calls itself, so a fresh connection announces it without being asked. */
  let name: string | null = options.playerName ?? null;
  /** Who this machine is playing as, so a fresh connection announces it without being asked. */
  let sheet: CharacterSheet | null = options.playerSheet ?? null;
  /** Names last seen in a roster, so a `left` can say who rather than which number. */
  let names = new Map<number, string>();

  const send = (message: LobbyMessage): void => {
    transport.send('event', MESSAGE_KINDS.lobby, encodeLobby(message));
  };

  const receive = (message: LobbyMessage): void => {
    switch (message.kind) {
      case 'roster':
        names = new Map(message.seats.map((seat) => [seat.playerId, seat.name]));
        options.onEvent({ kind: 'roster', seats: message.seats });
        return;

      case 'seat':
        token = message.token;
        options.onToken?.(message.token);
        options.onEvent({ kind: 'seated', playerId: message.playerId });
        return;

      case 'left':
        options.onNotice(
          leftNotice(
            names.get(message.playerId) ?? `Player ${String(message.playerId)}`,
            message.reason,
          ),
        );
        return;

      case 'ending':
        options.onEvent({ kind: 'ending', reason: message.reason });
        return;

      case 'rules':
        options.onEvent({ kind: 'rules', rules: message.rules });
        return;

      // Everything a guest sends. Hearing one back means a build is talking to itself.
      case 'ready':
      case 'rejoin':
      case 'leaving':
      case 'arrived':
      case 'name':
      case 'sheet':
        return;
    }
  };

  const isHost = (from: PeerId): boolean =>
    transport.peers().some((peer) => peer.id === from && peer.role === 'host');

  return {
    listeners: {
      ...silentListeners(),
      // A token carried over from a connection that died is worth nothing until there is a host to
      // present it to, so presenting is hung off the host arriving rather than off a timer.
      onPeerJoin: (peer): void => {
        if (peer.role !== 'host') return;
        if (token !== null) send({ kind: 'rejoin', token });
        // The seat comes back with the name it was held under, which is the name this machine had
        // when it dropped rather than the one it has now. Saying it again settles the two.
        if (name !== null) send({ kind: 'name', name });
        if (sheet !== null) send({ kind: 'sheet', sheet });
      },
      onMessage: (from, frame): void => {
        if (frame.kind !== MESSAGE_KINDS.lobby || !isHost(from)) return;
        receive(decodeLobby(frame.payload));
      },
    },

    setReady(ready: boolean): void {
      send({ kind: 'ready', ready });
    },

    setName(next: string): void {
      name = next;
      send({ kind: 'name', name: next });
    },

    setSheet(next: CharacterSheet): void {
      sheet = next;
      send({ kind: 'sheet', sheet: next });
    },

    announceArrival(): void {
      send({ kind: 'arrived' });
      if (name !== null) send({ kind: 'name', name });
      if (sheet !== null) send({ kind: 'sheet', sheet });
    },

    announceLeaving(): void {
      send({ kind: 'leaving' });
    },

    presentToken(): void {
      if (token === null) return;
      send({ kind: 'rejoin', token });
      if (name !== null) send({ kind: 'name', name });
      if (sheet !== null) send({ kind: 'sheet', sheet });
    },

    token: (): string | null => token,

    adoptToken(next: string): void {
      token = next;
    },
  };
}
